import styles from '../../../styles.module.scss';
import { ImageUploader } from '../../image-uploader';
import { useSaveBreakItemMutation } from '../api/save-break';
import { useSaveDefaultItemMutation } from '../api/save-default';
import { useSaveWheelItemMutation } from '../api/save-wheels';
import { useUpdateBreakItemMutation } from '../api/update-break';
import { useUpdateDefaultItemMutation } from '../api/update-default';
import { useUpdateWheelItemMutation } from '../api/update-wheels';
import { useWaitingStore } from '../waiting-context';
import { LaboursPartsSection } from './labours-parts-section-form';
import { RecordCheckBrakeItem, RecordCheckItem, RecordCheckWheelTyreItem, VhcItem } from 'features/vhc/model';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import { statusNames } from 'features/vhc/pages/record/vhc/model/constants';
import { ConditionStatus } from 'features/vhc/pages/record/vhc/model/types';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import SelectReact from 'react-select';

const customComponents = {
  Option: (props: any) => (
    <div
      {...props.innerProps}
      className={`${props.isFocused ? 'bg-light' : 'bg-white'} ${props.isSelected ? 'active' : ''} dropdown-item`}
      style={{ cursor: 'pointer', padding: '0.5rem 1rem' }}
    >
      <div className="d-flex align-items-center">
        <span
          className={`rounded-circle bg-${props.data.color} d-inline-block me-2`}
          style={{ width: '16px', height: '16px' }}
        ></span>
        <span>{props.data.label}</span>
      </div>
    </div>
  ),
  SingleValue: (props: any) => (
    <div className="d-flex align-items-center pb-4">
      <span
        className={`rounded-circle bg-${props.data.color} d-inline-block me-2`}
        style={{ width: '16px', height: '16px' }}
      ></span>
      <span>{props.data.label}</span>
    </div>
  ),
  IndicatorSeparator: () => null
};

export const VhcRecordBaseElements = () => {
  const { isWaiting, setIsWaiting } = useWaitingStore();

  const { watch, setValue, control, getValues } = useFormContext<VhcItem>();
  const { recordModalData, setRecordModalIsOpen } = useVhcStore();
  const { isReport, isComplete } = recordModalData || {};

  const images = watch('imageUrls');
  const itemId = watch('id');
  const isNew = watch('isNew');
  const [condition, setCondition] = useState<ConditionStatus>();
  const formCondition = watch('conditionId');

  useEffect(() => {
    if (formCondition != condition) {
      setCondition(formCondition);
    }
  }, [formCondition]);

  const { id } = useParams();

  const dataType = watch('formType');

  const queryClient = useQueryClient();

  const { mutateAsync: updateDefaultItem, isLoading: isUpdatingDefaultItem } = useUpdateDefaultItemMutation();
  const { mutateAsync: updateBreakItem, isLoading: isUpdatingBreakItem } = useUpdateBreakItemMutation();
  const { mutateAsync: updateWheelItem, isLoading: isUpdatingWheelItem } = useUpdateWheelItemMutation();
  const { mutateAsync: createDefaultItem, isLoading: isCreatingDefaultItem } = useSaveDefaultItemMutation();
  const { mutateAsync: createBrakeItem, isLoading: isCreatingBrakeItem } = useSaveBreakItemMutation();
  const { mutateAsync: createWheelItem, isLoading: isCreatingWheelItem } = useSaveWheelItemMutation();

  const isUpdating =
    isUpdatingDefaultItem ||
    isUpdatingBreakItem ||
    isUpdatingWheelItem ||
    isCreatingDefaultItem ||
    isCreatingBrakeItem ||
    isCreatingWheelItem;

  const conditionOptions = useMemo(() => {
    return [
      { value: ConditionStatus.OK, label: statusNames[ConditionStatus.OK], color: 'success' },
      { value: ConditionStatus.WARNING, label: statusNames[ConditionStatus.WARNING], color: 'warning' },
      { value: ConditionStatus.NOT_OK, label: statusNames[ConditionStatus.NOT_OK], color: 'danger' },
      { value: ConditionStatus.NOT_TESTED, label: statusNames[ConditionStatus.NOT_TESTED], color: 'secondary' }
    ];
  }, []);

  const handleConditionSelect = async (condition: ConditionStatus) => {
    if (!itemId || !id) return;

    const updatedData = {
      ...getValues(),
      conditionId: condition
    };

    setValue('conditionId', condition);

    let newItemId = itemId;
    setIsWaiting(true);

    if (isNew) {
      try {
        if (dataType === 'default') {
          newItemId = await createDefaultItem({
            logId: Number(id),
            requestBody: updatedData as RecordCheckItem
          });
        } else if (dataType === 'brake') {
          newItemId = await createBrakeItem({
            logId: Number(id),
            requestBody: updatedData as RecordCheckBrakeItem
          });
        } else if (dataType === 'wheelTyre') {
          newItemId = await createWheelItem({
            logId: Number(id),
            requestBody: updatedData as RecordCheckWheelTyreItem
          });
        }

        if (newItemId) {
          setValue('id', newItemId);
          setValue('isNew', false);
        }
      } catch (error) {
        console.error('Creating error:', error);
      }
    } else {
      try {
        if (dataType === 'default') {
          await updateDefaultItem({
            logId: Number(id),
            itemId: itemId,
            requestBody: updatedData as RecordCheckItem
          });
        } else if (dataType === 'brake') {
          await updateBreakItem({
            logId: Number(id),
            itemId: itemId,
            requestBody: updatedData as RecordCheckBrakeItem
          });
        } else if (dataType === 'wheelTyre') {
          await updateWheelItem({
            logId: Number(id),
            itemId: itemId,
            requestBody: updatedData as RecordCheckWheelTyreItem
          });
        }
      } catch (error) {
        console.error('Updating error:', error);
      }
    }

    await queryClient.invalidateQueries('ArticleByCodeQueryKey');
    setRecordModalIsOpen(
      {
        data: { ...updatedData, id: newItemId },
        isComplete: recordModalData?.isComplete,
        isReport: recordModalData?.isReport
      },
      true
    );

    setIsWaiting(false);
  };

  const handleSetPhotos = (photos: string[]) => {
    setValue('imageUrls', photos);
  };

  const handleRemovePhoto = (index: number) => {
    setValue('imageUrls', images?.filter((_, i) => i !== index) ?? []);
  };

  const isShowLaboursPartsSection = condition == ConditionStatus.NOT_OK || condition == ConditionStatus.WARNING;

  return (
    <>
      <Form.Group className="mb-5">
        <Form.Label className="d-block d-lg-none fs-7 mb-2">Condition</Form.Label>
        <div className="row align-items-center">
          <Form.Label className="col-lg-3 d-none d-lg-block fs-7 m-0">Condition</Form.Label>
          <div className="col-12 col-lg-9">
            <Controller
              name="conditionId"
              control={control}
              render={({ field }) => (
                <SelectReact
                  value={conditionOptions.find((option) => option.value == condition)}
                  isDisabled={isUpdating || isReport || isComplete || isWaiting}
                  options={conditionOptions}
                  onChange={async (option: any) => {
                    if (option && option.value != condition) {
                      setCondition(option.value);

                      handleConditionSelect(option.value);
                    }
                  }}
                  isSearchable={false}
                  placeholder=""
                  components={customComponents}
                  classNamePrefix="react-select"
                  className={`w-100 ${styles.conditionSelect}`}
                  styles={{
                    control: (base) => ({
                      ...base,
                      border: 'none',
                      boxShadow: 'none',
                      cursor: 'pointer',
                      backgroundColor: '#F5F8FA'
                    }),
                    menu: (base) => ({
                      ...base,
                      zIndex: 9999
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                      opacity: 0,
                      height: 0
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 10px'
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      color: '#7e8299'
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      paddingRight: '10px',
                      color: '#7e8299',
                      display: isReport || isComplete ? 'none' : 'block'
                    })
                  }}
                />
              )}
            />
          </div>
        </div>
      </Form.Group>

      <Form.Group className="mb-14">
        <Form.Label className="d-block d-lg-none fs-7 mb-2">Notes</Form.Label>
        <div className="row">
          <Form.Label className="col-lg-3 d-none d-lg-block fs-7 m-0 pt-4">Notes</Form.Label>
          <div className="col-12 col-lg-9">
            <Controller
              name="notes"
              control={control}
              render={({ field }) => (
                <Form.Control
                  disabled={isReport || isComplete}
                  as="textarea"
                  {...field}
                  rows={4}
                  value={field.value || ''}
                  className="w-100 resize-none form-control-solid"
                />
              )}
            />
          </div>
        </div>
      </Form.Group>
      {isShowLaboursPartsSection && <LaboursPartsSection />}
      <Form.Group className="mt-4">
        <h5 className=" fw-bold fs-3 text-dark-blue-500">Photos</h5>
        <ImageUploader
          disabled={isReport || isComplete}
          photos={images ?? []}
          handleSetPhotos={handleSetPhotos}
          onRemove={handleRemovePhoto}
        />
      </Form.Group>
    </>
  );
};
